/**
 * Zod mirror of `NoteDoc` (canonical TS source: `lib/memory/note-doc.ts`, Task 1).
 *
 * NoteDoc is both the human-facing render contract (PR2 viewer) and the machine-facing
 * representation (query engine / briefing read this, never re-parse compacted_text). This mirror
 * must accept BOTH a full v2 doc (populated overview + typed figure blocks) and the minimal legacy
 * `{ v: 1, overview: {...}, sections: [] }` shape — `v` is a plain number, not pinned to the current
 * NOTE_DOC_VERSION, so an older stored doc always parses.
 *
 * Every backend-sourced scalar is `.nullish()` (never `.optional()` — Python/Postgres NULL survives
 * as JSON null), every array defaults to `[]`, and every object is `.passthrough()` so a harmless
 * backend addition never fails validation (Global Constraints, spec 2026-07-17-conversation-note-
 * redesign-pr1).
 */
import { z } from "zod";

export const NoteStageSchema = z.enum(["idea", "in_progress", "review", "done", "blocked"]);
export type NoteStage = z.infer<typeof NoteStageSchema>;

export const KeyRefSchema = z
  .object({
    kind: z.string(),
    ref: z.string(),
  })
  .passthrough();
export type KeyRef = z.infer<typeof KeyRefSchema>;

export const PersonRelationSchema = z.enum(["author", "participant", "mentioned"]);
export type PersonRelation = z.infer<typeof PersonRelationSchema>;

export const NotePersonSchema = z
  .object({
    name: z.string(),
    relation: PersonRelationSchema,
  })
  .passthrough();
export type NotePerson = z.infer<typeof NotePersonSchema>;

export const NoteTableSchema = z
  .object({
    caption: z.string(),
    columns: z.array(z.string()).default([]),
    rows: z.array(z.array(z.string())).default([]),
  })
  .passthrough();
export type NoteTable = z.infer<typeof NoteTableSchema>;

export const NoteChartSeriesSchema = z
  .object({
    name: z.string(),
    values: z.array(z.number()).default([]),
  })
  .passthrough();
export type NoteChartSeries = z.infer<typeof NoteChartSeriesSchema>;

export const NoteChartSchema = z
  .object({
    caption: z.string(),
    kind: z.enum(["bar", "line"]),
    x_labels: z.array(z.string()).default([]),
    series: z.array(NoteChartSeriesSchema).default([]),
  })
  .passthrough();
export type NoteChart = z.infer<typeof NoteChartSchema>;

export const NoteActionItemSchema = z
  .object({
    text: z.string(),
    owner: z.string().nullish(),
    due: z.string().nullish(),
  })
  .passthrough();
export type NoteActionItem = z.infer<typeof NoteActionItemSchema>;

// A block is the atom the viewer renders: bullets, and/or one typed figure. Subheadings are part of
// the contract even though PR1's builder emits flat blocks — a consumer must never assume a block
// has bullets.
export const NoteBlockSchema = z
  .object({
    subheading: z.string().nullish(),
    bullets: z.array(z.string()).default([]),
    code: z
      .object({
        lang: z.string().nullish(),
        text: z.string(),
      })
      .passthrough()
      .nullish(),
    table: NoteTableSchema.nullish(),
    chart: NoteChartSchema.nullish(),
    // Structured action items (owner + due) for an action_items-section block. The builder dual-emits
    // this alongside `bullets`; the viewer prefers this and ignores bullets when present.
    action_items: z.array(NoteActionItemSchema).nullish(),
  })
  .passthrough();
export type NoteBlock = z.infer<typeof NoteBlockSchema>;

export const NoteSectionKindSchema = z.enum([
  "key_details",
  "decisions",
  "findings",
  "code",
  "preferences",
  "open_threads",
  "participants",
  "action_items",
  "discussion",
]);
export type NoteSectionKind = z.infer<typeof NoteSectionKindSchema>;

export const NoteSectionSchema = z
  .object({
    kind: NoteSectionKindSchema,
    blocks: z.array(NoteBlockSchema).default([]),
  })
  .passthrough();
export type NoteSection = z.infer<typeof NoteSectionSchema>;

export const NoteOverviewSchema = z
  .object({
    highlights: z.array(z.string()).default([]),
    where_it_stands: z.string().nullish(),
    stage: NoteStageSchema.nullish(),
    why_it_matters: z.string().nullish(),
    key_refs: z.array(KeyRefSchema).default([]),
    people: z.array(NotePersonSchema).default([]),
  })
  .passthrough();
export type NoteOverview = z.infer<typeof NoteOverviewSchema>;

export const NoteDocSchema = z
  .object({
    v: z.number(),
    overview: NoteOverviewSchema,
    sections: z.array(NoteSectionSchema).default([]),
  })
  .passthrough();
export type NoteDoc = z.infer<typeof NoteDocSchema>;
